/**
 * Pure parsing functions for extracting structured clinical data
 * (vitals, labs, imaging, medications) from clinical free text.
 *
 * Ported from JSX codebase (master:frontend/src/utils/clinicalDataParser.js)
 * to TypeScript with full type definitions.
 */

// ────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────

export type AbnormalFlag = 'high' | 'low' | null

export interface VitalSign {
  label: string
  value: string
  unit: string
  flag: AbnormalFlag
  icon: string
  refRange: string | null
}

export interface LabResult {
  label: string
  value: string
  unit: string
  flag: AbnormalFlag
  refRange: string
}

export interface LabGroup {
  name: string
  labs: LabResult[]
}

export interface ImagingFinding {
  modality: string
  finding: string
}

export interface MedicationCategories {
  continue: string[]
  hold: string[]
  adjust: string[]
  discontinue: string[]
  drug_interactions: string[]
  prophylaxis_gaps: string[]
}

export interface PanelSummary {
  raw: string
  oneLiner: string
  presentation: string
  admissionContext: string
}

export interface OrderSection {
  title: string
  content: string
  items: string[]
}

export interface PanelOrders {
  raw: string
  sections: OrderSection[]
}

export interface PanelResponse {
  summary: PanelSummary | null
  orders: PanelOrders | null
  vitals: VitalSign[] | null
  labs: LabGroup[] | null
  imaging: ImagingFinding[] | null
  medications: MedicationCategories | null
}

// ────────────────────────────────────────────────────────
// Vital Sign Patterns
// ────────────────────────────────────────────────────────

interface VitalPattern {
  re: RegExp
  label: string
  unit: string
  lo: number | null
  hi: number | null
  icon: string
}

const VITAL_PATTERNS: VitalPattern[] = [
  { re: /\bT(?:emp)?[\s:]+(\d+\.?\d*(?:\s*[-\u2013\u2014]\s*\d+\.?\d*)?)/i, label: 'Temp', unit: '\u00B0C', lo: 36.1, hi: 37.5, icon: '\uD83C\uDF21\uFE0F' },
  { re: /\bHR[\s:]+(\d+(?:\s*[-\u2013\u2014]\s*\d+)?)/i, label: 'HR', unit: 'bpm', lo: 60, hi: 100, icon: '\u2764\uFE0F' },
  { re: /\bBP[\s:]+(\d+(?:\s*[-\u2013\u2014]\s*\d+)?\/\d+(?:\s*[-\u2013\u2014]\s*\d+)?)/i, label: 'BP', unit: 'mmHg', lo: null, hi: null, icon: '\uD83E\uDEC0' },
  { re: /\bRR[\s:]+(\d+(?:\s*[-\u2013\u2014]\s*\d+)?)/i, label: 'RR', unit: '/min', lo: 12, hi: 20, icon: '\uD83E\uDEC1' },
  { re: /\bSpO2[\s:]+(\d+(?:\s*[-\u2013\u2014]\s*\d+)?)%?/i, label: 'SpO2', unit: '%', lo: 92, hi: 100, icon: '\uD83D\uDCA7' },
  { re: /\bFiO2[\s:]+(\d+(?:\s*[-\u2013\u2014]\s*\d+)?)%?/i, label: 'FiO2', unit: '%', lo: null, hi: null, icon: '\uD83C\uDF2C\uFE0F' },
  { re: /\b(?:Wt|Weight)[\s:]+(\d+\.?\d*(?:\s*[-\u2013\u2014]\s*\d+\.?\d*)?)\s*kg/i, label: 'Weight', unit: 'kg', lo: null, hi: null, icon: '\u2696\uFE0F' },
  { re: /\b[Pp]ain[\s:]+(\d+(?:\s*[-\u2013\u2014]\s*\d+)?)\/10/i, label: 'Pain', unit: '/10', lo: null, hi: null, icon: '\uD83D\uDE23' },
]

export const parseVitals = (text: string | null | undefined): VitalSign[] | null => {
  if (!text) return null
  const vitals: VitalSign[] = []

  for (const { re, label, unit, lo, hi, icon } of VITAL_PATTERNS) {
    const m = text.match(re)
    if (m) {
      const rawValue = m[1]
      let flag: AbnormalFlag = null

      if (label === 'BP') {
        const parts = rawValue.split('/')
        const sysNums = parts[0].split(/[-\u2013\u2014]/).map(v => parseInt(v.trim(), 10)).filter(v => !isNaN(v))
        const diaNums = parts[1] ? parts[1].split(/[-\u2013\u2014]/).map(v => parseInt(v.trim(), 10)).filter(v => !isNaN(v)) : []
        const sysMax = Math.max(...sysNums)
        const sysMin = Math.min(...sysNums)
        const diaMax = diaNums.length > 0 ? Math.max(...diaNums) : 0
        const diaMin = diaNums.length > 0 ? Math.min(...diaNums) : 60
        if (sysMax > 140 || diaMax > 90) flag = 'high'
        else if (sysMin < 90 || diaMin < 60) flag = 'low'
      } else if (label === 'Temp') {
        // Auto-detect Fahrenheit vs Celsius: values > 50 are assumed Fahrenheit
        const nums = rawValue.split(/[-\u2013\u2014]/).map(v => parseFloat(v.trim())).filter(v => !isNaN(v))
        if (nums.length > 0) {
          const minVal = Math.min(...nums)
          const maxVal = Math.max(...nums)
          if (maxVal > 50) {
            // Fahrenheit: normal range 97.8-99.5
            if (minVal < 97.8) flag = 'low'
            else if (maxVal > 99.5) flag = 'high'
          } else {
            // Celsius: normal range 36.1-37.5
            if (minVal < 36.1) flag = 'low'
            else if (maxVal > 37.5) flag = 'high'
          }
        }
      } else {
        const nums = rawValue.split(/[-\u2013\u2014]/).map(v => parseFloat(v.trim())).filter(v => !isNaN(v))
        if (lo != null && hi != null && nums.length > 0) {
          const minVal = Math.min(...nums)
          const maxVal = Math.max(...nums)
          if (minVal < lo) flag = 'low'
          else if (maxVal > hi) flag = 'high'
        }
      }

      const refRange = lo != null && hi != null
        ? `${lo}\u2013${hi}`
        : label === 'BP' ? '90/60\u2013140/90' : null

      vitals.push({ label, value: rawValue, unit, flag, icon, refRange })
    }
  }

  return vitals.length > 0 ? vitals : null
}

// ────────────────────────────────────────────────────────
// Lab Patterns
// ────────────────────────────────────────────────────────

interface LabPattern {
  re: RegExp
  label: string
  unit: string
  lo: number
  hi: number
}

interface LabGroupDef {
  name: string
  patterns: LabPattern[]
}

const LAB_GROUPS: LabGroupDef[] = [
  {
    name: 'CBC',
    patterns: [
      { re: /\bWBC[\s:]+(\d+\.?\d*)/i, label: 'WBC', unit: 'K/\u00B5L', lo: 4.5, hi: 11.0 },
      { re: /\b(?:Hgb|[Hh]emoglobin)[\s:]+(\d+\.?\d*)/i, label: 'Hemoglobin', unit: 'g/dL', lo: 12.0, hi: 17.5 },
      { re: /\b(?:Hct|[Hh]ematocrit)[\s:]+(\d+\.?\d*)/i, label: 'Hematocrit', unit: '%', lo: 36, hi: 51 },
      { re: /\b[Pp]latelets?[\s:]+(\d+)/i, label: 'Platelets', unit: 'K/\u00B5L', lo: 150, hi: 400 },
    ],
  },
  {
    name: 'BMP',
    patterns: [
      { re: /\b[Ss]odium[\s:]+(\d+)/i, label: 'Sodium', unit: 'mEq/L', lo: 136, hi: 145 },
      { re: /\b[Pp]otassium[\s:]+(\d+\.?\d*)/i, label: 'Potassium', unit: 'mEq/L', lo: 3.5, hi: 5.0 },
      { re: /\b[Cc]reatinine[\s:]+(\d+\.?\d*)/i, label: 'Creatinine', unit: 'mg/dL', lo: 0.6, hi: 1.2 },
      { re: /\bBUN[\s:]+(\d+)/i, label: 'BUN', unit: 'mg/dL', lo: 7, hi: 20 },
      { re: /\b[Gg]lucose[\s:]+(\d+)/i, label: 'Glucose', unit: 'mg/dL', lo: 70, hi: 100 },
      { re: /\b[Mm]agnesium[\s:]+(\d+\.?\d*)/i, label: 'Magnesium', unit: 'mg/dL', lo: 1.7, hi: 2.2 },
      { re: /\b[Pp]hosphorus[\s:]+(\d+\.?\d*)/i, label: 'Phosphorus', unit: 'mg/dL', lo: 2.5, hi: 4.5 },
    ],
  },
  {
    name: 'Cardiac',
    patterns: [
      { re: /\b[Tt]roponin[\s:]+(\d+\.?\d*)/i, label: 'Troponin', unit: 'ng/mL', lo: 0, hi: 0.04 },
      { re: /\bBNP[\s:]+(\d+\.?\d*)/i, label: 'BNP', unit: 'pg/mL', lo: 0, hi: 100 },
    ],
  },
  {
    name: 'Inflammatory',
    patterns: [
      { re: /\b[Pp]rocalcitonin[\s:]+(\d+\.?\d*)/i, label: 'Procalcitonin', unit: 'ng/mL', lo: 0, hi: 0.1 },
      { re: /\b[Ll]actate[\s:]+(\d+\.?\d*)/i, label: 'Lactate', unit: 'mmol/L', lo: 0.5, hi: 2.0 },
      { re: /\bCRP[\s:]+(\d+\.?\d*)/i, label: 'CRP', unit: 'mg/L', lo: 0, hi: 10 },
      { re: /\bESR[\s:]+(\d+)/i, label: 'ESR', unit: 'mm/hr', lo: 0, hi: 20 },
    ],
  },
  {
    name: 'ABG / VBG',
    patterns: [
      { re: /\bpH[\s:]+(\d+\.\d+)/i, label: 'pH', unit: '', lo: 7.35, hi: 7.45 },
      { re: /\bpCO2[\s:]+(\d+\.?\d*)/i, label: 'pCO2', unit: 'mmHg', lo: 35, hi: 45 },
      { re: /\bpO2[\s:]+(\d+\.?\d*)/i, label: 'pO2', unit: 'mmHg', lo: 80, hi: 100 },
    ],
  },
  {
    name: 'Coagulation',
    patterns: [
      { re: /\bINR[\s:]+(\d+\.?\d*)/i, label: 'INR', unit: '', lo: 0.8, hi: 1.1 },
    ],
  },
  {
    name: 'Endocrine',
    patterns: [
      { re: /\bTSH[\s:]+(\d+\.?\d*)/i, label: 'TSH', unit: 'mIU/L', lo: 0.4, hi: 4.0 },
    ],
  },
  {
    name: 'Hepatic',
    patterns: [
      { re: /\bAST[\s:]+(\d+)/i, label: 'AST', unit: 'U/L', lo: 10, hi: 40 },
      { re: /\bALT[\s:]+(\d+)/i, label: 'ALT', unit: 'U/L', lo: 7, hi: 56 },
      { re: /\b(?:Alk ?Phos|ALP)[\s:]+(\d+)/i, label: 'Alk Phos', unit: 'U/L', lo: 44, hi: 147 },
      { re: /\b(?:T\.?\s*Bili|[Tt]otal [Bb]ilirubin)[\s:]+(\d+\.?\d*)/i, label: 'Total Bilirubin', unit: 'mg/dL', lo: 0.1, hi: 1.2 },
      { re: /\b[Aa]lbumin[\s:]+(\d+\.?\d*)/i, label: 'Albumin', unit: 'g/dL', lo: 3.5, hi: 5.5 },
    ],
  },
]

export const parseLabs = (text: string | null | undefined): LabGroup[] | null => {
  if (!text) return null
  const groups: LabGroup[] = []

  for (const group of LAB_GROUPS) {
    const labs: LabResult[] = []
    for (const { re, label, unit, lo, hi } of group.patterns) {
      const m = text.match(re)
      if (m) {
        const numVal = parseFloat(m[1])
        let flag: AbnormalFlag = null
        if (!isNaN(numVal)) {
          if (numVal < lo) flag = 'low'
          else if (numVal > hi) flag = 'high'
        }
        labs.push({ label, value: m[1], unit, flag, refRange: `${lo}\u2013${hi}` })
      }
    }
    if (labs.length > 0) {
      groups.push({ name: group.name, labs })
    }
  }

  return groups.length > 0 ? groups : null
}

// ────────────────────────────────────────────────────────
// Imaging Patterns
// ────────────────────────────────────────────────────────

interface ImagingPattern {
  re: RegExp
  modality: string
}

const IMAGING_PATTERNS: ImagingPattern[] = [
  { re: /\b(?:CXR|[Cc]hest\s*(?:X-?ray|x-?ray|XR))\s*[:\-\u2013]\s*(.*?)(?:\n|$)/i, modality: 'CXR' },
  { re: /\b(?:CT\s*(?:chest|abdomen|head|brain|pelvis|angio|PE\s*protocol|scan)?)\s*[:\-\u2013]\s*(.*?)(?:\n|$)/i, modality: 'CT' },
  { re: /\bMRI\s*(?:brain|spine|abdomen)?\s*[:\-\u2013]\s*(.*?)(?:\n|$)/i, modality: 'MRI' },
  { re: /\b(?:TTE|[Tt]ransthoracic\s*[Ee]cho|[Ee]chocardiogram|[Ee]cho)\s*[:\-\u2013]\s*(.*?)(?:\n|$)/i, modality: 'Echo' },
  { re: /\b(?:US|[Uu]ltrasound|RUQ\s*US|[Dd]oppler)\s*[:\-\u2013]\s*(.*?)(?:\n|$)/i, modality: 'Ultrasound' },
  { re: /\b(?:EKG|ECG|[Ee]lectrocardiogram)\s*[:\-\u2013]\s*(.*?)(?:\n|$)/i, modality: 'EKG' },
  { re: /\b(?:KUB)\s*[:\-\u2013]\s*(.*?)(?:\n|$)/i, modality: 'KUB' },
]

export const parseImaging = (text: string | null | undefined): ImagingFinding[] | null => {
  if (!text) return null
  const findings: ImagingFinding[] = []

  for (const { re, modality } of IMAGING_PATTERNS) {
    const m = text.match(re)
    if (m && m[1]?.trim()) {
      findings.push({ modality, finding: m[1].trim() })
    }
  }

  return findings.length > 0 ? findings : null
}

// ────────────────────────────────────────────────────────
// Medication Reconciliation JSON Parser
// ────────────────────────────────────────────────────────

interface MedReconInput {
  medications_reviewed?: Array<{
    name: string
    status?: string
    rationale?: string
  }>
  interactions?: Array<{
    drugs: string
    severity?: string
    clinical_significance?: string
  }>
  gaps?: Array<{
    category: string
    recommendation?: string
  }>
  duplications?: Array<{
    drugs: string
    recommendation?: string
  }>
}

export const parseMedReconJSON = (jsonStr: string | MedReconInput | null | undefined): MedicationCategories | null => {
  try {
    if (!jsonStr) return null

    const data: MedReconInput = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr
    if (!data || (!data.medications_reviewed && !data.interactions && !data.gaps)) return null

    const result: MedicationCategories = {
      continue: [], hold: [], adjust: [], discontinue: [],
      drug_interactions: [], prophylaxis_gaps: [],
    }

    if (Array.isArray(data.medications_reviewed)) {
      for (const med of data.medications_reviewed) {
        const line = med.rationale
          ? `**${med.name}** \u2014 ${med.rationale}`
          : `**${med.name}**`
        const status = (med.status || '').toLowerCase().replace(/\s+/g, '_')
        if (status === 'continue') result.continue.push(line)
        else if (status === 'hold') result.hold.push(line)
        else if (status === 'adjust') result.adjust.push(line)
        else if (status === 'discontinue') result.discontinue.push(line)
        else if (status === 'needs_review') result.adjust.push(`${line} \u26A0\uFE0F Needs Review`)
        else result.continue.push(line)
      }
    }

    if (Array.isArray(data.interactions)) {
      for (const ix of data.interactions) {
        const severity = ix.severity ? ` (${ix.severity})` : ''
        result.drug_interactions.push(`**${ix.drugs}**${severity} \u2014 ${ix.clinical_significance || ''}`)
      }
    }

    if (Array.isArray(data.gaps)) {
      for (const gap of data.gaps) {
        result.prophylaxis_gaps.push(`**${gap.category}** \u2014 ${gap.recommendation || ''}`)
      }
    }

    if (Array.isArray(data.duplications)) {
      for (const dup of data.duplications) {
        result.drug_interactions.push(`**Duplication: ${dup.drugs}** \u2014 ${dup.recommendation || ''}`)
      }
    }

    const hasAny = Object.values(result).some(arr => arr.length > 0)
    return hasAny ? result : null
  } catch (err) {
    console.error('[clinicalDataParser] Failed to parse drug interactions:', err)
    return null
  }
}

// ────────────────────────────────────────────────────────
// Panel Response Parser
// ────────────────────────────────────────────────────────

const EMPTY_RESPONSE: PanelResponse = {
  summary: null, orders: null, vitals: null, labs: null, imaging: null, medications: null,
}

export const parsePanelResponse = (output: string | null | undefined): PanelResponse => {
  if (!output) return { ...EMPTY_RESPONSE }

  try {
    const clean = output.replace(/<!--\s*RELAY:.*?-->/gs, '').trim()

    const summaryMatch = clean.match(/## PATIENT SUMMARY\s*\n([\s\S]*?)(?=## ADMIT ORDERS|## MEDICATION RECONCILIATION|$)/i)
    const ordersMatch = clean.match(/## ADMIT ORDERS\s*\n([\s\S]*?)(?=## MEDICATION RECONCILIATION|$)/i)
    const medsMatch = clean.match(/## MEDICATION RECONCILIATION\s*\n([\s\S]*?)(?=## CLINICAL DATA|## PATIENT SUMMARY|$)/i)

    let summary: PanelSummary | null = null
    if (summaryMatch) {
      const text = summaryMatch[1].trim()
      const oneLinerMatch = text.match(/\*\*One-liner:\*\*\s*(.*)/i)
      const presentationMatch = text.match(/\*\*Presentation:\*\*\s*([\s\S]*?)(?=\*\*Admission Context|$)/i)
      const contextMatch = text.match(/\*\*Admission Context[:\s]*\*\*\s*([\s\S]*?)$/i)

      let admCtx = contextMatch ? contextMatch[1].trim() : ''
      admCtx = admCtx
        .replace(/\n*\s*\*?\*?\s*(?:Vitals?(?:\s*\([^)]*\))?|(?:Key\s+)?Labs?(?:\s*\([^)]*\))?|Clinical Data|Imaging|Diagnostics)\s*[:*][\s\S]*/i, '')
        .trim()

      summary = {
        raw: text,
        oneLiner: oneLinerMatch ? oneLinerMatch[1].trim() : '',
        presentation: presentationMatch ? presentationMatch[1].trim() : '',
        admissionContext: admCtx,
      }
    }

    let orders: PanelOrders | null = null
    if (ordersMatch) {
      const text = ordersMatch[1].trim()
      const sections: OrderSection[] = []
      const sectionRegex = /### (.+)\n([\s\S]*?)(?=### |\s*$)/g
      let match: RegExpExecArray | null
      while ((match = sectionRegex.exec(text)) !== null) {
        sections.push({
          title: match[1].trim(),
          content: match[2].trim(),
          items: match[2].trim().split('\n')
            .map(line => line.replace(/^[-*]\s*/, '').trim())
            .filter(line => line.length > 0),
        })
      }
      orders = { raw: text, sections }
    }

    let vitals: VitalSign[] | null = null
    let labs: LabGroup[] | null = null
    let imaging: ImagingFinding[] | null = null
    let medications: MedicationCategories | null = null

    try { vitals = parseVitals(clean) } catch (err) { console.warn('[clinicalDataParser] Vitals parsing failed:', err) }
    try { labs = parseLabs(clean) } catch (err) { console.warn('[clinicalDataParser] Labs parsing failed:', err) }
    try { imaging = parseImaging(clean) } catch (err) { console.warn('[clinicalDataParser] Imaging parsing failed:', err) }

    if (medsMatch) {
      try {
        const medsText = medsMatch[1].trim()
        const medCategories = ['Continue', 'Hold', 'Adjust', 'Discontinue', 'Drug Interactions', 'Prophylaxis Gaps']
        const parsed: Record<string, string[]> = {}

        for (const cat of medCategories) {
          const key = cat.toLowerCase().replace(/\s+/g, '_')
          const catRegex = new RegExp(`###\\s*\\**${cat}\\**\\s*\\n([\\s\\S]*?)(?=###\\s|$)`, 'i')
          const catMatch = medsText.match(catRegex)
          parsed[key] = catMatch
            ? catMatch[1].trim().split('\n')
                .map(line => line.replace(/^[-*\u25CF\u2022]\s*/, '').trim())
                .filter(line => line.length > 0)
            : []
        }
        medications = parsed as unknown as MedicationCategories
      } catch (err) { console.warn('[clinicalDataParser] Medication parsing failed:', err) }
    } else {
      // Fallback: inline med decision format
      const medLines = clean.match(/^[-*\u25CF\u2022]\s*\*\*.*?\*\*.*?\u2192.*?\*\*(CONTINUE|HOLD|ADJUST|DISCONTINUE)\*\*/gim)
      if (medLines && medLines.length > 0) {
        const parsed: MedicationCategories = {
          continue: [], hold: [], adjust: [], discontinue: [],
          drug_interactions: [], prophylaxis_gaps: [],
        }
        for (const line of medLines) {
          const cleanLine = line.replace(/^[-*\u25CF\u2022]\s*/, '').trim()
          if (/CONTINUE/i.test(cleanLine)) parsed.continue.push(cleanLine)
          else if (/HOLD/i.test(cleanLine)) parsed.hold.push(cleanLine)
          else if (/ADJUST/i.test(cleanLine)) parsed.adjust.push(cleanLine)
          else if (/DISCONTINUE/i.test(cleanLine)) parsed.discontinue.push(cleanLine)
        }
        medications = parsed
      }
    }

    return { summary, orders, vitals, labs, imaging, medications }
  } catch (err) {
    console.error('[clinicalDataParser] Panel response parsing failed:', err)
    return { ...EMPTY_RESPONSE }
  }
}
